import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Layers,
  Wind,
  Thermometer,
  Box,
  Clock,
  FileText,
  ChevronRight,
  BookOpen
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { useQuery } from "@tanstack/react-query";

interface ProcessDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  processRecord: any;
  onOpenLamination?: () => void;
}

export function ProcessDetailsDialog({
  open,
  onOpenChange,
  processRecord,
  onOpenLamination,
}: ProcessDetailsDialogProps) {
  const processName = processRecord?.manufacturing_processes?.name || "";
  const processCode = processRecord?.manufacturing_processes?.code || "";
  const workOrder = processRecord?.work_orders;
  const plybookId = workOrder?.plybook?.id || workOrder?.plybook_id;

  const isLamination = processName === "material_layup" || processName === "Laminado" || processCode === "LAMI";
  const isVacuum = processName === "vacuum_bagging";
  const isAutoclave = processName === "autoclave_curing";

  // Cargar info del plybook
  const { data: plybookInfo } = useQuery({
    queryKey: ["plybook-info", plybookId],
    queryFn: async () => {
      if (!plybookId) return null;
      const { data } = await api.from("plybooks").select("id,name,code").eq("id", plybookId).single();
      return data;
    },
    enabled: !!plybookId && isLamination && open,
  });

  const { data: plies } = useQuery({
    queryKey: ["plybook-plies-details", plybookId],
    queryFn: async () => {
      if (!plybookId) return [];
      const { data, error } = await api
        .from("plybook_plies")
        .select("*")
        .eq("plybook_id", plybookId)
        .order("ply_number");
      if (error) throw error;
      return data || [];
    },
    enabled: !!plybookId && isLamination && open,
  });

  const { data: recipes } = useQuery({
    queryKey: ["available-recipes"],
    queryFn: async () => {
      const { data } = await api
        .from("production_recipes")
        .select("id, name, notes")
        .eq("is_active", true)
        .limit(5);
      return data || [];
    },
    enabled: isAutoclave && open,
  });

  const getProcessTitle = () => {
    if (isLamination) return "Instrucciones de Laminado";
    if (isVacuum) return "Instrucciones de Vacio";
    if (isAutoclave) return "Instrucciones de Autoclave";
    return "Detalles del Proceso";
  };

  const getProcessIcon = () => {
    if (isLamination) return <Layers className="w-5 h-5" />;
    if (isVacuum) return <Wind className="w-5 h-5" />;
    if (isAutoclave) return <Thermometer className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getProcessIcon()}
            {getProcessTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-muted/50 p-3 rounded-lg">
          <p className="font-medium">{workOrder?.order_number}</p>
          <p className="text-sm text-muted-foreground">{workOrder?.product_name}</p>
          {workOrder?.mold && (
            <p className="text-sm flex items-center gap-1 mt-1">
              <Box className="w-3 h-3" />
              Molde: {workOrder.mold.name}
            </p>
          )}
        </div>

        <Separator />

        {isLamination && (
          <div className="space-y-4">
            {plybookInfo && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  <span className="font-medium">Plybook: {plybookInfo.name}</span>
                </div>
                <Badge variant="outline">{plies?.length || 0} capas</Badge>
              </div>
            )}

            {plies && plies.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Secuencia de capas:</p>
                {plies.map((ply: any) => (
                  <Card key={ply.id} className="border-l-4" style={{ borderLeftColor: ply.color || "#888" }}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold text-lg">#{ply.ply_number}</span>
                          <span className="ml-2 text-sm">{ply.material_name}</span>
                        </div>
                        {ply.zone && (
                          <Badge variant="secondary" className="text-xs">{ply.zone}</Badge>
                        )}
                      </div>
                      {ply.orientation && (
                        <p className="text-xs text-muted-foreground mt-1">Orientacion: {ply.orientation}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No hay capas definidas</p>
            )}

            {onOpenLamination && (
              <Button onClick={onOpenLamination} className="w-full" size="lg">
                <BookOpen className="w-4 h-4 mr-2" />
                Abrir Modo Laminador
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            )}
          </div>
        )}

        {isVacuum && (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <Clock className="w-5 h-5" />
                <span className="font-medium">Tiempo minimo: 15 minutos</span>
              </div>
              <Separator className="bg-blue-200 dark:bg-blue-800" />
              <div className="space-y-2">
                <p className="text-sm font-medium">Datos requeridos al completar:</p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>Presion de vacio (mbar o %)</li>
                  <li>Observaciones (opcional)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {isAutoclave && (
          <div className="space-y-4">
            <div className="bg-orange-50 dark:bg-orange-950 p-4 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                <Thermometer className="w-5 h-5" />
                <span className="font-medium">Ciclo de Autoclave</span>
              </div>
              <Separator className="bg-orange-200 dark:bg-orange-800" />
              <div className="space-y-2">
                <p className="text-sm font-medium">Parametros tipicos:</p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>Temperatura maxima (C)</li>
                  <li>Presion (bar)</li>
                  <li>Tiempo de curado (minutos)</li>
                </ul>
              </div>
            </div>
            {recipes && recipes.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Recetas disponibles:</p>
                <div className="space-y-1">
                  {recipes.map((r: any) => (
                    <div key={r.id} className="text-sm p-2 bg-muted rounded">{r.name}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!isLamination && !isVacuum && !isAutoclave && (
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <p className="font-medium">{processRecord?.manufacturing_processes?.name}</p>
              {processRecord?.manufacturing_processes?.description && (
                <p className="text-sm text-muted-foreground mt-2">
                  {processRecord.manufacturing_processes.description}
                </p>
              )}
            </div>
          </div>
        )}

        <Separator />

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
